import { lazy, Suspense } from 'react';
import { PermissionGuard } from '@emfood/shared';
import { Outlet, createRoute, SearchSchemaInput } from '@tanstack/react-router';

import { PERMISSIONS } from 'src/api/roles-config';
import DashboardLayout from 'src/layouts/dashboard';

import AppAuthGuard from 'src/components/auth/app-auth-guard';
import { SplashScreen, LoadingScreen } from 'src/components/loading-screen';

import InvoicesListSearchParams from 'src/sections/invoices/invoices-list-search-params';
import ReviewInvoiceSearchParams from 'src/sections/invoices/review-invoice/review-invoice-search-params';
import PendingInvoiceListSearchParams from 'src/sections/invoices/pending-invoices/pending-invoice-list-search-params';

import { rootRoute } from './root';

const InvoicesList = lazy(() => import('src/sections/invoices/view'));
const UploadInvoices = lazy(() => import('src/sections/invoices/upload-invoices/view'));
const InvoiceDetails = lazy(() => import('src/sections/invoices/invoice-details/view'));
const PendingInvoices = lazy(() => import('src/sections/invoices/pending-invoices/view'));
const EditInvoice = lazy(() => import('src/sections/invoices/edit-invoice/view'));
const ReviewInvoice = lazy(() => import('src/sections/invoices/review-invoice/view'));
const InvoiceAnalytics = lazy(() => import('src/sections/invoices/analytics/view'));

const invoicesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'invoices',
  component: () => (
    <AppAuthGuard splashScreen={<SplashScreen />}>
      <PermissionGuard requiredPermissions={[PERMISSIONS.INVOICES_READ]}>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </PermissionGuard>
    </AppAuthGuard>
  ),
});

const invoicesIndexRoute = createRoute({
  getParentRoute: () => invoicesRoute,
  path: '/',
  component: InvoicesList,
  validateSearch: (search: unknown & SearchSchemaInput) =>
    InvoicesListSearchParams.validateSync(search),
});

const pendingInvoicesRoute = createRoute({
  getParentRoute: () => invoicesRoute,
  path: '/pending',
  component: PendingInvoices,
  validateSearch: (search: unknown & SearchSchemaInput) =>
    PendingInvoiceListSearchParams.validateSync(search),
});

const invoicesUploadRoute = createRoute({
  getParentRoute: () => invoicesRoute,
  path: 'upload',
  component: UploadInvoices,
});

const invoicesAnalyticsRoute = createRoute({
  getParentRoute: () => invoicesRoute,
  path: 'analytics',
  component: InvoiceAnalytics,
});

const invoicesDetailsRoute = createRoute({
  getParentRoute: () => invoicesRoute,
  path: '$id',
  component: InvoiceDetails,
});

const invoicesEditRoute = createRoute({
  getParentRoute: () => invoicesRoute,
  path: '$id/edit',
  component: EditInvoice,
});

const invoicesReviewRoute = createRoute({
  getParentRoute: () => invoicesRoute,
  path: '$id/review',
  component: ReviewInvoice,
  validateSearch: (search: unknown & SearchSchemaInput) =>
    ReviewInvoiceSearchParams.validateSync(search),
});

export const invoicesRouteTree = invoicesRoute.addChildren([
  invoicesIndexRoute,
  invoicesUploadRoute,
  invoicesAnalyticsRoute,
  invoicesDetailsRoute,
  pendingInvoicesRoute,
  invoicesEditRoute,
  invoicesReviewRoute,
]);
